import { Activity, ActivityType, DEFAULT_PAGE_INFO } from '@apeiron/common'
import { PlanetFilterInput } from '@apeiron/planet'
import {
  ChainNetworkType,
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { activitySchema } from '@src/deserialize/yup/activity'
import graphql from '@src/graphql/query/getPlanetActivities'
import { isFilterInputEmpty } from '@src/util/apeiron/planetList'
import { sub } from 'date-fns'
import { useCallback, useState } from 'react'

const ACTIVITIES_WITHIN_DAYS = 3650

export const useGetPlanetActivities = (): Hook => {
  const [getPlanetActivities, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<Activity[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        activityType = [],
        filterInput = {},
        pageInput = {},
        skipTotal = false
      } = request

      const currentTime = new Date()

      const variables = {
        filter: {
          activityType,
          beginTime: sub(currentTime, {
            days: ACTIVITIES_WITHIN_DAYS
          }).toISOString(),
          endTime: currentTime.toISOString(),
          networks: [ChainNetworkType.Ronin]
        },
        filterInput: isFilterInputEmpty(filterInput) ? {} : filterInput,
        pagingInput: serializePagingInput(pageInput),
        skipTotal
      }

      const response = await getPlanetActivities({
        variables
      })

      const activities = deserializeListData<Activity>(response, activitySchema)

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(activities)
      setPageInfo(pageInfo)

      return {
        data: activities,
        pageInfo
      }
    },
    [getPlanetActivities]
  )

  return {
    data,
    loading,
    pageInfo,
    fetch
  }
}

export type Request = {
  activityType?: ActivityType[]
  filterInput?: PlanetFilterInput
  pageInput?: PageInput
  skipTotal?: boolean
}

type Response = {
  data: Activity[]
  pageInfo: PageInfo
}

type Hook = {
  data: Activity[]
  loading: boolean
  pageInfo: PageInfo
  fetch: (request: Request) => Promise<Response>
}
